import React from "react";
import {
  format,
  addMonths,
  startOfWeek,
  endOfWeek,
  addDays,
  endOfMonth,
  isSameMonth,
  isToday,
  startOfDay
} from "date-fns";
import { useTranslation } from "react-i18next";
import pt from "date-fns/locale/pt";
import enUS from "date-fns/locale/en-US";
import "./YearlyCalendar.css";

const dateLocales = { pt, en: enUS };

const YearlyCalendar = ({ absences = [], user, holidays = [], currentYear, onYearChange }) => {
  const { t, i18n } = useTranslation();

  const handlePrevYear = () => onYearChange((prev) => prev - 1);
  const handleNextYear = () => onYearChange((prev) => prev + 1);

  const myAbsences = absences
    .filter((abs) => abs.status === "Accepted" && abs.user_id === user?.id)
    .map((abs) => ({
      ...abs,
      start: startOfDay(new Date(abs.start_date)),
      end: startOfDay(new Date(abs.end_date)),
    }));

  const getAbsenceForDay = (day) => {
    return myAbsences.find((abs) => day >= abs.start && day <= abs.end);
  };

  const weekDays = t("components.YearlyCalendar.weekdays", { defaultValue: "D,S,T,Q,Q,S,S" }).split(",");
  const currentLocale = dateLocales[i18n.language?.startsWith("pt") ? "pt" : "en"] || pt;

  const renderMonths = () => {
    const months = [];
    const firstDayOfYear = new Date(currentYear, 0, 1);

    for (let i = 0; i < 12; i++) {
      const monthStart = addMonths(firstDayOfYear, i);
      const monthEnd = endOfMonth(monthStart);
      const startDate = startOfWeek(monthStart, { weekStartsOn: 0 });
      const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 });

      const days = [];
      let day = startDate;

      while (day <= endDate) {
        const cloneDay = new Date(day);
        const absence = getAbsenceForDay(cloneDay);
        const isCurrentMonth = isSameMonth(cloneDay, monthStart);

        let dayStyle = {};
        let title = "";

        let weekendClass = "";
        const dayOfWeek = cloneDay.getDay();
        if (dayOfWeek === 0) {
          weekendClass = "is-sunday";
        } else if (dayOfWeek === 6) {
          weekendClass = "is-saturday";
        }

        const formattedCloneDay = format(new Date(Date.UTC(cloneDay.getFullYear(), cloneDay.getMonth(), cloneDay.getDate())), 'yyyy-MM-dd');
        const holidayData = holidays.find((h) => h.date === formattedCloneDay);

        if (isCurrentMonth && holidayData) {
           dayStyle = {
             backgroundColor: "rgba(239, 68, 68, 0.15)",
             color: "var(--danger-red)",
             fontWeight: "bold",
           };
           title = holidayData.label;
        } 
        else if (isCurrentMonth && absence) {
          dayStyle = {
            backgroundColor: "var(--brand-primary)", 
            color: "var(--white)",
            fontWeight: "bold",
          };
          title = absence.absence_type;
        }

        days.push(
          <div
            key={cloneDay.toString()}
            title={title}
            className={`day-cell ${isCurrentMonth ? "in-month" : "out-of-month"} ${
              isToday(cloneDay) ? "is-today" : ""
            } ${weekendClass}`}
            style={dayStyle}
          >
            {format(cloneDay, "d")}
          </div>
        );
        day = addDays(day, 1);
      }

      months.push(
        <div key={i} className="mini-month">
          <div className="month-name">
            {format(monthStart, "MMMM", { locale: currentLocale })}
          </div>
          <div className="weekdays-row">
            {weekDays.map((d, index) => (
              <div key={index}>{d}</div>
            ))}
          </div>
          <div className="days-grid">{days}</div>
        </div>
      );
    }
    return months;
  };

  return (
    <div className="yearly-calendar-wrapper h-100">
      <div className="yearly-header">
        <button onClick={handlePrevYear} className="year-nav-btn">&lt;</button>
        <h4 className="m-0 fw-bold" style={{ color: "var(--text-primary)" }}>{currentYear}</h4>
        <button onClick={handleNextYear} className="year-nav-btn">&gt;</button>
      </div>
      <div
        className="months-grid"
        style={{ maxHeight: "calc(100% - 60px)", overflowY: "auto", paddingRight: "5px" }}
      >
        {renderMonths()}
      </div>
    </div>
  );
};

export default YearlyCalendar;